import {
  Cesium3DTileFeature,
  Cesium3DTileset,
  Color,
  defined,
  JulianDate,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
} from "@cesium/engine";

import {
  DitapAlert,
  EditRemove,
  EditUtils,
  MeasurementVolume,
  PopupMultiSelect,
  Utils,
} from "@cesium/widgets";

/**
 * 객체 삭제를 위한 사용자 입력(마우스/키보드) 클래스.<br>
 * User input (mouse/keyboard) class for object deletion.
 *
 * @alias InteractionRemove
 * @constructor
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * // viewer에 모델 추가
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
 * );
 * const model = await Ditap.Model.fromGltfAsync{
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * // InteractionRemove on/off 실행 시 EditRemove 초기화 필수
 * const editRemove = new Ditap.EditRemove(viewer);
 *
 * const interactionRemove = new Ditap.InteractionRemove;
 * interactionRemove.editRemove = editRemove;
 */
class InteractionRemove {
  constructor(options) {
    options = this.checkSelectOptions(options);
    this._editRemove = undefined;
    this._handler = undefined;
    this._isOn = false;
    this._infoLabelEntity = undefined;
    this._windowEvent = {};
    this._multiSelectPopup = undefined;
    this._removeAlert = undefined;
    this._mousePosition = undefined;
    this._buttonElement = undefined;
    this._selectedObject = undefined;
    this._highlightedObjects = [];
    this._hoverColor = options.hoverColor;
    this._selectedColor = options.selectedColor;
    this._isMobile = undefined; // 모바일 여부
  }

  /**
   * 객체 삭제를 위한 편집 기능 클래스.</br>
   * Editing feature class for object deletion.
   * @type {EditRemove}
   */
  set editRemove(editRemove) {
    if (editRemove instanceof EditRemove || editRemove === undefined) {
      this._editRemove = editRemove;
    } else {
      console.error("Type doesn't match");
    }
  }
  get editRemove() {
    return this._editRemove;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(boolean) {
    this._isOn = boolean;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 윈도우 이벤트 객체 배열.</br>
   * Objects containing Windows events.
   * @type {object[]}
   */
  set windowEvent(windowEvent) {
    this._windowEvent = windowEvent;
  }
  get windowEvent() {
    return this._windowEvent;
  }

  /*
   * 선택된 객체가 여러개인 경우 표시되는 팝업.</br>
   * Popup displayed when there are multiple selected objects.
   * @type {DitapPopup}
   */
  get multiSelectPopup() {
    return this._multiSelectPopup;
  }
  set multiSelectPopup(multiSelectPopup) {
    this._multiSelectPopup = multiSelectPopup;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 삭제 알림창.</br>
   * A waring alert.
   * @type {DitapAlert}
   */
  set removeAlert(removeAlert) {
    this._removeAlert = removeAlert;
  }
  get removeAlert() {
    return this._removeAlert;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }
  get buttonElement() {
    return this._buttonElement;
  }

  /**
   * DitapViewer에서 선택한 객체.<br>
   * Selected object in DitapViewer.
   * @type {object}
   */
  set selectedObject(selectedObject) {
    this._selectedObject = selectedObject;
  }
  get selectedObject() {
    return this._selectedObject;
  }

  /**
   * 강조된 객체 배열.<br>
   * Highlighted object array.
   * @type {object[]}
   */
  set highlightedObjects(highlightedObjects) {
    this._highlightedObjects = highlightedObjects;
  }
  get highlightedObjects() {
    return this._highlightedObjects;
  }

  /**
   * 마우스 over 시 변경 될 색상.
   * Color to be changed when mouse is over.
   * @type {Color}
   */
  set hoverColor(hoverColor) {
    this._hoverColor = hoverColor;
  }
  get hoverColor() {
    return this._hoverColor;
  }

  /**
   * 마우스 클릭 시 변경 될 색상.
   * Color to be changed when the mouse is clicked.
   * @type {Color}
   */
  set selectedColor(selectedColor) {
    this._selectedColor = selectedColor;
  }
  get selectedColor() {
    return this._selectedColor;
  }

  /**
   * 핸들러 on/off 기능
   * Function on and off.
   */
  toggle() {
    if (!this.isOn) {
      this.on();
    } else {
      this.off();
    }
  }

  /**
   * 객체 삭제 핸들러 실행. (EditRemove 초기화 필수.)</br>
   * Execute object removal handler. (EditRemove initialization is required.)
   */
  on() {
    if (this.editRemove && !this.isOn) {
      this._isMobile = Utils.detectDeviceType();
      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      const viewer = this.editRemove.viewer;
      this.isOn = true;
      this.handler = new ScreenSpaceEventHandler(viewer.canvas);

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.editRemove.viewer.container.querySelector(".cesium-viewer"),
        "삭제할 대상을 왼 클릭 또는 터치하세요"
      );
      this.infoLabel = Utils.createInfoLabel(
        this.editRemove.viewer.container.querySelector(".cesium-viewer"),
        "삭제할 대상을 클릭하세요"
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.createMobileEvent();
        this.infoLabel.style.display = "none";
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      // 핸들러 실행
      this.createLeftClickHandler();
      this.createRightClickHandler();
      this.createWindowEvent();
      this.createResizeEvent();

      // 다중 선택 박스 생성
      this.multiSelectPopup = new PopupMultiSelect(this, viewer);

      const objAlert = {
        type: "warning",
        title: "모델 삭제",
        content1: "선택한 모델을 삭제하시겠습니까?",
        content2: "모델은 삭제되면 복구할 수 없습니다.",
        buttons: "OKANDCANCEL",
        open: false,
        callback: () => {},
        closeCallback: () => {
          if (this.selectedObject) {
            this.setSelectedObjectToOriginalColor();
            this.selectedObject = undefined;
          }
        },
      };
      // 삭제 Alert 생성
      this.removeAlert = new DitapAlert(objAlert, this);
    }
  }

  /**
   * 객체 삭제 핸들러 종료. (EditRemove 초기화 필수.)</br>
   * End object removal handler. (EditRemove initialization is required.)
   */
  off() {
    if (this.editRemove && this.isOn) {
      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");

      //도움말 라벨 제거
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      if (this.selectedObject) {
        if (this.selectedObject instanceof Cesium3DTileset) {
          this.selectedObject.style = undefined;
        } else {
          const originColor = EditUtils.getOriginalColor(this.selectedObject);
          EditUtils.changeColor(this.selectedObject, originColor);
        }
      }

      this.highlightedObjects.filter((object) => {
        if (object instanceof Cesium3DTileset) {
          object.style = undefined;
        } else {
          const originColor = EditUtils.getOriginalColor(object);
          EditUtils.changeColor(object, originColor);
        }
      });

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
      }
      this.removeWindowEvent();

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      this.multiSelectPopup.destroy();
      this.removeAlert.destroy();

      this.handler = undefined;
      this.isOn = false;
      this.infoLabelEntity = undefined;
      this.clientX = undefined;
      this.clientY = undefined;
      this.windowEvent = {};
      this.mousePosition = undefined;
      this.multiSelectPopup = undefined;
      this.removeAlert = undefined;
      this.editRemove = undefined;
      this.cancelBtn = undefined;
      this._isMobile = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
    }
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.off();
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
    }
    this.cancelEvent = undefined;
    this.cancelBtn = undefined;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();

      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 선택된 객체 기존 색상으로 변경. <br>
   * change selected object to original color.
   */
  setSelectedObjectToOriginalColor() {
    // 선택한 기존 객체 색상 변경
    if (this.selectedObject && this.selectedObject) {
      const originalColor = EditUtils.getOriginalColor(this.selectedObject);
      EditUtils.changeColor(this.selectedObject, originalColor);
      this.selectedObject = undefined;
    }
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const viewer = this.editRemove.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      if (this.isOn) {
        // 삭제할 객체 선택
        const pickedObjects = viewer.scene.drillPick(
          e.position,
          undefined,
          1,
          1
        );

        // 선택한 기존 객체 색상 변경
        this.setSelectedObjectToOriginalColor();

        // 호버 객체 색상 변경
        this.highlightedObjects = EditUtils.setHighlightObjects(
          this.highlightedObjects,
          this.hoverColor,
          this.selectedObject,
          []
        );

        // 다중선택 팝업 닫기
        this.multiSelectPopup.close();

        // 첫번째 선택된 객체
        const firstPickedObject = pickedObjects[0];

        // 객체배열에서 중복 제외
        const uniquePickedObjects = this.getUniquePickedObjects(
          this.editRemove.viewer,
          pickedObjects
        );

        if (uniquePickedObjects.length > 1) {
          // 1. 선택된 객체가 다중일 경우

          // 다중선택팝업에 객체 리스트 추가
          this.multiSelectPopup.addMultiList(
            uniquePickedObjects,
            this.removeAlert
          );
          this.multiSelectPopup.open();
        } else if (defined(firstPickedObject)) {
          // 체적 계산 중인 경우
          if (
            this.editRemove.viewer.ditapStorage.getDitapEntity(
              firstPickedObject
            ) instanceof MeasurementVolume &&
            this.editRemove.viewer.ditapStorage.getDitapEntity(
              firstPickedObject
            ).positions.length === 0
          ) {
            // 경고창 생성
            const warningAelrt = new DitapAlert({
              type: "warning",
              title: "경고",
              content1: "체적 계산 중인 경우 삭제가 불가합니다.",
              buttons: "ONLYOK",
              open: false,
              callback: (alert) => {
                alert.destroy();
              },
              closeCallback: (alert) => {
                alert.destroy();
              },
            });
            warningAelrt.open();
            return;
          }

          // 선택한 객체 저장
          this.selectedObject = firstPickedObject;

          // 선택 객체 색상 변경
          this.highlightedObjects = this.highlightedObjects.filter(
            (obj) => obj !== firstPickedObject
          );
          const selectedColor = EditUtils.getColorByObjectType(
            this.selectedObject,
            this.selectedColor
          );
          EditUtils.setOriginalColor(this.selectedObject);
          EditUtils.changeColor(this.selectedObject, selectedColor);

          // 삭제
          this.editRemove.removeModelByType(
            firstPickedObject,
            this.removeAlert
          );
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const handler = this.handler;
    const viewer = this.editRemove.viewer;

    let lastMoveTime;

    handler.setInputAction((e) => {
      if (!lastMoveTime) {
        lastMoveTime = JulianDate.now();
        return;
      }

      const currentTime = JulianDate.now();
      const timeDifference = JulianDate.secondsDifference(
        currentTime,
        lastMoveTime
      );

      if (timeDifference >= 0.05) {
        const position = Utils.pickPosition(e.endPosition, viewer);

        if (defined(position)) {
          this.mousePosition = position;

          // 마우스 무브시 라벨 이동
          Utils.changeLabelLocation(
            this.editRemove.viewer,
            position,
            this.infoLabelEntity
          );

          const objects = viewer.scene.drillPick(
            e.endPosition,
            undefined,
            1,
            1
          );

          const uniquePickedObjects = this.getUniquePickedObjects(
            this.editRemove.viewer,
            objects
          );

          this.highlightedObjects = EditUtils.setHighlightObjects(
            this.highlightedObjects,
            this.hoverColor,
            this.selectedObject,
            uniquePickedObjects
          );
        } else {
          this.infoLabelEntity.classList.add("hide");
        }
        lastMoveTime = currentTime;
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 선택된 객체 중 중복 제외한 객체 배열 조회.</br>
   * Get an array of objects excluding duplicates among the selected objects.
   * @param {object[]} objects 선택된 객체 배열.</br>Selected objects.
   * @returns {object[]} 중복 제외된 객체 배열.</br> Array of objects excluding duplicates.
   */
  getUniquePickedObjects(viewer, pickedObjects) {
    // * ENTITY - 선택된 다이탭 객체들중에서 중복을 제외한 객체 반환
    const pickedDitapObject = [];
    if (pickedObjects.length > 0) {
      pickedObjects.forEach((pickedObject) => {
        let object = pickedObject;
        if (viewer.ditapStorage.getDitapEntity(pickedObject)) {
          object = viewer.ditapStorage.getDitapEntity(pickedObject);
        } else if (pickedObject instanceof Cesium3DTileFeature) {
          object = pickedObject.tileset || pickedObject.primitive;
        }

        pickedDitapObject.push(object);
      });
    }
    // 중복을 제외한 SET 객체
    const set = new Set(pickedDitapObject);
    const uniquePickedObjects = [...set];
    return uniquePickedObjects;
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.off();
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 윈도우 객체에 이벤트 추가.<br>
   * Add events to window object
   */
  createWindowEvent() {
    // keyDown 이벤트 생성
    window.addEventListener(
      "keydown",
      (this.windowEvent["keydown"] = (e) => {
        this.terminate(e);
      })
    );
  }

  /*
   * Excape 키 다운 시 실행 이벤트.</br>
   * Exit a handler for deleting objects.
   * @param {Event} event 키보드 이벤트.</br> Keyboard event.
   */
  terminate(Event) {
    if (Event.key === "Escape") {
      this.off();
    }
  }

  /*
   * 윈도우 객체에 추가된 이벤트 제거.</br>
   * Remove Events added on Windows Objects.
   */
  removeWindowEvent() {
    const keys = Object.keys(this.windowEvent);
    for (let i = 0; i < keys.length; i++) {
      window.removeEventListener(keys[i], this.windowEvent[keys[i]]);
    }
  }

  /**
   * 선택 옵션 확인.<br>
   * check the object of select.
   * @param {object} options 선택 옵션.<br>object of select options.
   * @returns {object} 확인된 선택 옵션.<br>Verified select options.
   */
  checkSelectOptions(options) {
    const newOptions = {};
    if (typeof options === "object") {
      if (!options.hoverColor || !(options.hoverColor instanceof Color)) {
        newOptions.hoverColor = Color.fromCssColorString("#BDBDBD");
      } else {
        newOptions.hoverColor = options.hoverColor;
      }
      if (!options.selectedColor || !(options.selectedColor instanceof Color)) {
        newOptions.selectedColor = Color.fromCssColorString("#9DCFFF");
      } else {
        newOptions.selectedColor = options.selectedColor;
      }
    } else {
      newOptions.hoverColor = Color.fromCssColorString("#BDBDBD");
      newOptions.selectedColor = Color.fromCssColorString("#9DCFFF");
    }
    return newOptions;
  }
}
export default InteractionRemove;
